import { FixedSizeGrid, FixedSizeList } from "react-window";
import Avatar from "@mui/material/Avatar";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ImageListItem from "@mui/material/ImageListItem";
import ImageListItemBar from "@mui/material/ImageListItemBar";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import { grey } from "@mui/material/colors";
import Icon from "@mdi/react";
import { mdiVote } from "@mdi/js";
import { styled } from "@mui/material/styles";
import { useQueryVoteResult } from "../../hooks/top18ApiHooks";
import { useWindowSize } from "../../hooks/windowHooks";
import { useAppStore } from "../../store/appStore";
import { sidebarWidth } from "../Menu";
import { Loading } from "../Loading";
import { pad } from "../../fn/pad";

const StyledBadge = styled(Badge)(({ theme }) => ({
	"& .MuiBadge-badge": {
		right: -6,
		top: 18,
		padding: "0 4px",
	},
}));

const useRowColumn = () => {
	const { width, height } = useWindowSize();

	return {
		columnCount: width >= 1200 ? 6 : height >= 800 ? 4 : 6,
		rowCount: width >= 1200 ? 2 : height >= 800 ? 3 : 2,
	};
};

const DataList = () => {
	const { width, height } = useWindowSize();
	const sortEnabled = useAppStore((state) => state.sortEnabled);
	const listOption = useAppStore((state) => state.listOption);

	const { isLoading, isError, error, data } = useQueryVoteResult({
		listOption,
		sortEnabled,
	});

	const { columnCount, rowCount } = useRowColumn();

	if (isLoading || !width || !height) return <Loading />;

	if (isError) return <div>錯誤: {error.message}</div>;

	if (!data.records) return <div>錯誤: 未能存取資料。</div>;

	return width >= 750 ? (
		<FixedSizeGrid
			columnCount={columnCount}
			columnWidth={(width >= 1080 ? width - sidebarWidth : width) / columnCount}
			height={height}
			rowCount={rowCount}
			rowHeight={height / rowCount}
			width={width}
		>
			{DataGridCell}
		</FixedSizeGrid>
	) : (
		<FixedSizeList
			height={height}
			itemCount={data.records.length}
			itemSize={150}
			width={width}
		>
			{FixedSizeListItem}
		</FixedSizeList>
	);
};

const DataGridCell = ({ columnIndex, rowIndex, style }) => {
	const { width } = useWindowSize();
	const sortEnabled = useAppStore((state) => state.sortEnabled);
	const listOption = useAppStore((state) => state.listOption);

	const { data } = useQueryVoteResult({
		listOption,
		sortEnabled,
	});

	const { columnCount } = useRowColumn();

	const index = columnIndex + rowIndex * columnCount;

	if (index >= data?.records.length) return <div />;

	const { id, votes, rank, totalVotes } = data?.records[index];

	return (
		<ImageListItem key={`item-${id}`} style={style}>
			<img
				src={`${process.env.PUBLIC_URL}/images/${
					listOption === "f" ? "f" : "m"
				}/top21/${id}.jpg`}
				alt={`#${pad(id, 2)}`}
				loading="lazy"
			/>
			{width < 1545 ? (
				<ImageListItemBar
					title={
						<StyledBadge
							badgeContent={rank}
							color={rank > 8 ? "error" : "success"}
						>
							<Avatar
								variant="square"
								sx={{ bgcolor: grey[300], color: "#000" }}
							>
								{id}
							</Avatar>
						</StyledBadge>
					}
					subtitle={
						<Box pt={2}>
							<Chip 
								icon={<Icon path={mdiVote} size={1} color="#FFF" />}
								label={`${totalVotes} (${votes.toFixed(2)}%)`}
								sx={{ color: "#FFF" }}
							/>
						</Box>
					}
					position="bottom"
				/>
			) : (
				<ImageListItemBar
					title={
						<StyledBadge
							badgeContent={rank}
							color={rank > 8 ? "error" : "success"}
						>
							<Avatar
								variant="square"
								sx={{ bgcolor: grey[300], color: "#000" }}
							>
								{id}
							</Avatar>
						</StyledBadge>
					}
					actionIcon={
						<Stack direction="row" spacing={1} alignItems="center">
							<Box pr={1}>
								<Chip 
									icon={<Icon path={mdiVote} size={1} color="#FFF" />}
									label={`${totalVotes} (${votes.toFixed(2)}%)`}
									sx={{ color: "#FFF" }}
								/>
							</Box>
						</Stack>
					}
				/>
			)}
		</ImageListItem>
	);
};

const FixedSizeListItem = ({ index, style }) => {
	const sortEnabled = useAppStore((state) => state.sortEnabled);
	const listOption = useAppStore((state) => state.listOption);

	const { data } = useQueryVoteResult({
		listOption,
		sortEnabled,
	});

	if (index >= data?.records.length) return <div />;

	const { id, votes, rank, totalVotes } = data?.records[index];

	return (
		<div key={id} style={style}>
			<ListItem divider>
				<ListItemAvatar>
					<Avatar
						alt={`#${pad(id, 2)}`}
						src={`${process.env.PUBLIC_URL}/images/${
							listOption === "f" ? "f" : "m"
						}/top21/${id}.jpg`}
						sx={{ width: 128, height: 128 }}
					/>
				</ListItemAvatar>
				<Box ml={2}>
					<ListItemText
						primary={
							<StyledBadge
								badgeContent={rank}
								color={rank > 8 ? "error" : "success"}
							>
								<Avatar
									variant="square"
									sx={{ bgcolor: grey[300], color: "#000" }}
								>
									{id}
								</Avatar>
							</StyledBadge>
						}
						secondary={
							<>
								<Box pt={2}>
									<Stack direction="row" spacing={2}>
										<Chip 
											icon={<Icon path={mdiVote} size={1} />}
											label={`${totalVotes} (${votes.toFixed(2)}%)`}
										/>
									</Stack>
								</Box>
							</>
						}
					/>
				</Box>
			</ListItem>
		</div>
	);
};

const VotePage = () => {
	return <DataList />;
};

export default VotePage;
